import React, { memo, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useDocsPageContext } from '../../../hooks/useDocsPageContext';

import { withFoldersAndDocsActions } from '../../../hocs/withFoldersAndDocsActions';

import {
    SENT_FOLDER_OR_DOC_DATA_LS_KEY,
    ARCHIVED_FOLDER_OR_DOC_DATA_LS_KEY,
} from '../../../constants/docs';

import MoveToPanel from '../MoveToPanel/MoveToPanel';
import DocsColumnHeader from '../DocsColumnHeader/DocsColumnHeader';
import SelectedItemsActionBar from '../SelectedItemsActionBar/SelectedItemsActionBar';
import DraggableFolderOrDocCard from '../DraggableFolderOrDocCard/DraggableFolderOrDocCard';
import ArchiveMultipleItemsModal from '../ArchiveMultipleItemsModal/ArchiveMultipleItemsModal';

const FoldersAndDocsList = ({
    data,
    setActionModal,
    setSuccessSentToAlert,
    setSuccessArchiveAlert,
}) => {
    const { folderId } = useParams();
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get('search');

    const [selectedItemsActionModal, setSelectedItemsActionModal] = useState({ action: null });

    const { requestKeysToMutate, currentRequestKeyToMutate, selectedItems, setSelectedItems } =
        useDocsPageContext();

    useEffect(() => {
        clearAllSelectedItems();
    }, [folderId, searchQuery]);

    useEffect(() => {
        // if the doc was archived from Doc Detail page show success archive alert
        const savedSuccessArchiveDocData = localStorage.getItem(ARCHIVED_FOLDER_OR_DOC_DATA_LS_KEY);
        if (savedSuccessArchiveDocData) {
            try {
                const parsedData = JSON.parse(savedSuccessArchiveDocData);
                setSuccessArchiveAlert(parsedData);
            } catch (e) {
            } finally {
                localStorage.removeItem(ARCHIVED_FOLDER_OR_DOC_DATA_LS_KEY);
            }
        }
    }, []);

    useEffect(() => {
        // if the doc was sent to another organization from Doc Detail page show success sent to org alert
        const sentToOrgItemData = localStorage.getItem(SENT_FOLDER_OR_DOC_DATA_LS_KEY);
        if (sentToOrgItemData) {
            try {
                const parsedData = JSON.parse(sentToOrgItemData);
                setSuccessSentToAlert(parsedData);
            } catch (e) {
            } finally {
                localStorage.removeItem(SENT_FOLDER_OR_DOC_DATA_LS_KEY);
            }
        }
    }, []);

    const clearAllSelectedItems = () => setSelectedItems({});

    const selectAllItems = () => {
        const currentPageItems = data.reduce((acc, item) => ({ ...acc, [item.id]: item }), {});

        setSelectedItems((prev) => ({ ...prev, ...currentPageItems }));
    };

    const selectedItemsCount = Object.keys(selectedItems)?.length || 0;

    return (
        <>
            <div className="relative">
                {!!data?.length && (
                    <>
                        <div className="md:mb-1">
                            {selectedItemsCount > 0 && (
                                <SelectedItemsActionBar
                                    selectedItems={selectedItems}
                                    selectedItemsCount={selectedItemsCount}
                                    currentRequestKeyToMutate={currentRequestKeyToMutate}
                                    requestKeysToMutate={requestKeysToMutate}
                                    setSelectedItemsActionModal={setSelectedItemsActionModal}
                                    clearAllSelectedItems={clearAllSelectedItems}
                                />
                            )}
                            <div className="hidden md:block">
                                {selectedItemsCount === 0 && (
                                    <DocsColumnHeader selectAllItems={selectAllItems} />
                                )}
                            </div>
                        </div>

                        <ul className="flex flex-col gap-1">
                            {data.map((item) => (
                                <DraggableFolderOrDocCard
                                    key={item.id}
                                    item={item}
                                    isSelected={!!selectedItems[item.id]}
                                    setActionModal={setActionModal}
                                />
                            ))}
                        </ul>
                    </>
                )}

                {selectedItemsActionModal.action === 'move' && (
                    <MoveToPanel
                        itemsToMove={selectedItems}
                        clearAllSelectedItems={clearAllSelectedItems}
                        currentRequestKeyToMutate={currentRequestKeyToMutate}
                        requestKeysToMutate={requestKeysToMutate}
                        viewType="popup"
                        onClose={() => setSelectedItemsActionModal({ action: null })}
                    />
                )}
            </div>

            {selectedItemsActionModal.action === 'multiple-archive' && (
                <ArchiveMultipleItemsModal
                    items={selectedItems}
                    requestKeysToMutate={requestKeysToMutate}
                    currentRequestKeyToMutate={currentRequestKeyToMutate}
                    setSuccessArchiveAlert={setSuccessArchiveAlert}
                    clearAllSelectedItems={clearAllSelectedItems}
                    onClose={() => setSelectedItemsActionModal({ action: null })}
                />
            )}
        </>
    );
};

export default memo(withFoldersAndDocsActions(FoldersAndDocsList));
